import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createTag } from 'api/tags';
import { queryKeys } from 'constants/queryKeys';
import { tagManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TTag, TUpsertTagRequest } from 'types';
import { toErrorMessage } from './utils';

type UseCreateTagOptions = {
  onSuccess?: (tag: TTag) => void;
};

type UseCreateTagResult = {
  create: (request: TUpsertTagRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useCreateTag(
  options: UseCreateTagOptions = {},
): UseCreateTagResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = tagManagementStrings.create;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (request: TUpsertTagRequest) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return createTag(request);
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TAGS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      setError(undefined);
      options.onSuccess?.(tag);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.submitFailed));
    },
  });

  const create = (request: TUpsertTagRequest) => {
    setError(undefined);
    mutation.mutate(request);
  };

  return {
    create,
    isSubmitting: mutation.isPending,
    error,
  };
}
